import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState } from 'react'

import Dialog from '../common/dialog'
import { Pocket } from '../../utils/dexie/db'
import { FormError, SystemUser, UserSessionType } from '../../types'
import { NotificationsContext } from '../../contexts'
import { requestAccessToken } from '../../api/auth.api'
import SystemUserDataForm from './system-user-data-form'
import { UserContext } from '../../contexts/user.context'
import { AuthContext } from '../../contexts/auth.context'
import SystemUserDataTable from './system-user-data-table'
import { handleValidateSystemUserFormData } from './utils'
import { updateSystemUserData } from '../../api/system-users.api'

export const UpdateSystemUserModal = ({
  pockets,
  systemUserData,
  systemUsersData,
  handleCloseSession,
  handleOpenStateOfModal,
  handleResetPocketFilterData
}: {
  pockets?: Array<Pocket>
  handleCloseSession: () => void
  systemUsersData: Array<SystemUser>
  handleResetPocketFilterData: () => void
  systemUserData: SystemUser | Partial<SystemUser>
  handleOpenStateOfModal: (openState: boolean) => void
}) => {
  const { isAuth } = useContext(AuthContext)
  const { userSession } = useContext(UserContext)
  const { t } = useTranslation(['system-users-management', 'common'])
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const [errors, setErrors] = useState<FormError>({})
  const [editSystemUser, setEditSystemUser] = useState<boolean>(false)
  const [selectedPocketId, setSelectedPocketId] = useState<string>('')
  const [selectedPocketName, setSelectedPocketName] = useState<string>('')
  const [dialogUpdateUserIsLoading, setDialogUpdateUserIsLoading] = useState<boolean>(false)
  const [systemUserDataToUpdate, setSystemUserDataToUpdate] = useState<Partial<SystemUser>>({
    role: '',
    username: '',
    lastName: '',
    firstName: ''
  })

  useEffect(() => {
    if (systemUserData.username) {
      setSystemUserDataToUpdate((systemUserDataPrevState: Partial<SystemUser>) => ({
        ...systemUserDataPrevState,
        ...systemUserData
      }))

      if (systemUserData.belongsTo !== 'holdings') {
        for (const pocket of pockets || []) {
          if (systemUserData.adminOfPockets?.includes(String(pocket.id))) {
            setSelectedPocketId(String(pocket?.id))
            setSelectedPocketName(String(pocket?.name))
          }
        }
      }
    }
  }, [systemUserData, pockets])

  const handleValidateUpdateSystemUserForm = async () => {
    setErrors({})
    const systemUserFormDataIsValid = handleValidateSystemUserFormData(systemUserDataToUpdate as SystemUser, systemUsersData, userSession as UserSessionType, 'update', t)

    if (!systemUserFormDataIsValid.formDataIsValid) {
      if (Array.isArray(pockets) && pockets?.length > 1 && !selectedPocketId) {
        return setErrors({ ...systemUserFormDataIsValid.formErrors, selectedPocketId: t('newSystemUserModal.systemUserDataForm.errors.emptyPocketSelection') })
      } else {
        return setErrors({ ...systemUserFormDataIsValid.formErrors })
      }
    }

    if (Array.isArray(pockets) && pockets?.length > 1 && !selectedPocketId) {
      return setErrors({ selectedPocketId: t('newSystemUserModal.systemUserDataForm.errors.emptyPocketSelection') })
    }

    try {
      setDialogUpdateUserIsLoading(true)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const systemUserToUpdateId: string = systemUserDataToUpdate._id as string
      const pocketSelectedToAssign = Array.isArray(pockets) && pockets?.length === 1 ? String(pockets[0].id) : selectedPocketId

      await updateSystemUserData({ ...systemUserDataToUpdate, adminOfPockets: [pocketSelectedToAssign] }, systemUserToUpdateId, accessTokenData.data.accessToken)
      delete systemUserDataToUpdate.selected

      const pocketDataToSet = pockets?.find((pocketData: Pocket) => String(pocketData.id) === String(pocketSelectedToAssign))
      setSelectedPocketId(String(pocketDataToSet?.id))
      setSelectedPocketName(String(pocketDataToSet?.name))

      setEditSystemUser(false)
      setDialogUpdateUserIsLoading(false)
      handleResetPocketFilterData()
      updateNewNotificationStatus?.(true, 'system-users-updated')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogUpdateUserIsLoading(false)
    }
  }

  return (
    <Dialog
      errors={errors}
      handleDialogAction={() => {
        if (editSystemUser === false) {
          setEditSystemUser(true)
          return
        }

        handleValidateUpdateSystemUserForm()
      }}
      dialogIsLoading={dialogUpdateUserIsLoading}
      actionButtonText={editSystemUser ? t('updateSystemUserModal.systemUserDataForm.actionButtonText') : t('updateSystemUserModal.systemUserDataTable.actionButtonText')}
      dialogTitle={editSystemUser ? t('updateSystemUserModal.systemUserDataForm.title') : t('updateSystemUserModal.systemUserDataTable.title')}
      handleDialogOpenState={(openState: boolean) => {
        handleOpenStateOfModal(openState)

        if (openState === false) {
          setErrors({})
          setEditSystemUser(false)
          setSystemUserDataToUpdate({
            role: '',
            username: '',
            lastName: '',
            firstName: ''
          })
        }
      }}
    >
      {editSystemUser ? (
        <SystemUserDataForm
          errors={errors}
          pockets={pockets}
          actionToDo="update"
          selectedPocketId={selectedPocketId}
          systemUserData={systemUserDataToUpdate}
          handleSetSystemUserData={(newSystemUserData: Partial<SystemUser>) =>
            setSystemUserDataToUpdate((prevSystemUserData: Partial<SystemUser>) => ({
              ...prevSystemUserData,
              ...newSystemUserData
            }))
          }
          handlePocketToAssignToUser={(pocketId: string) => setSelectedPocketId(pocketId)}
        />
      ) : (
        <SystemUserDataTable
          systemUserData={systemUserDataToUpdate}
          selectedPocketName={selectedPocketName}
        />
      )}
    </Dialog>
  )
}
